import { makeAutoObservable, runInAction } from 'mobx';
import {
  CONFIRMATION_DEFAULT_EXPIRES_IN,
  CONFIRMATION_METHOD_KIND,
  IAuthDataSource,
  PHONE_VERIFICATION_ERROR_KEY,
  PhoneVerificationError,
  UNEXPECTED_ERROR_KEY,
} from '../../../../../infrastructure/types/features/auth/auth-types';
import {
  LoadingStatus,
  SubmittingStatus,
} from '../../../../../../shared/infrastructure/types/common/common-types';
import { ResponseStatus } from '../../../../../../shared/infrastructure/types/api/api-types';
import { RequestPhoneVerificationParams } from '../../../../../infrastructure/types/features/auth/auth-contracts';
import { RegisterManager } from './RegisterManager';
import { YandexCloudManager } from './YandexCloudManager';

class QrConfirmationManager {
  registrationId?: string;
  accessToken?: string;
  phoneCode?: PhoneCode;
  codeError?: string;
  verificationError?: string;
  isWasRequest = false;

  confirmationSecondsLeft = 0;
  confirmationQrUrl: Url = ' ' as Url;
  confirmationRequestInterval?: number;
  confirmationTimerId?: ReturnType<typeof setInterval>;

  constructor(
    private registerManager: RegisterManager,
    private yandexCloudManager: YandexCloudManager,
    private datasource: IAuthDataSource,
    private setVerificationPrepareStatus: (status: LoadingStatus) => void,
    private setFormStatus: (status: SubmittingStatus) => void,
  ) {
    makeAutoObservable(this, {});
  }

  get confirmationTimer(): string {
    const safe = Math.max(0, this.confirmationSecondsLeft);
    const minutes = Math.floor(safe / 60);
    const rest = safe % 60;
    return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
  }

  get isConfirmationExpired(): boolean {
    return this.confirmationSecondsLeft <= 0;
  }

  get verificationErrorKey(): string | undefined {
    if (!this.verificationError) {
      return undefined;
    }
    return (
      PHONE_VERIFICATION_ERROR_KEY[this.verificationError as PhoneVerificationError] ??
      UNEXPECTED_ERROR_KEY
    );
  }

  setIsWasRequest(value: boolean) {
    this.isWasRequest = value;
  }

  private startConfirmationTimer(seconds?: number) {
    this.stopConfirmationTimer();
    this.confirmationSecondsLeft =
      seconds ?? this.confirmationSecondsLeft ?? CONFIRMATION_DEFAULT_EXPIRES_IN;
    if (this.confirmationSecondsLeft <= 0) {
      return;
    }
    this.confirmationTimerId = setInterval(() => this.tickConfirmationTimer(), 1000);
  }

  private tickConfirmationTimer() {
    if (this.confirmationSecondsLeft > 0) {
      this.confirmationSecondsLeft -= 1;
      return;
    }
    this.stopConfirmationTimer();
  }

  stopConfirmationTimer() {
    if (this.confirmationTimerId) {
      clearInterval(this.confirmationTimerId);
      this.confirmationTimerId = undefined;
    }
  }

  checkCodeByID() {
    if (!this.registrationId || !this.accessToken) {
      return;
    }

    this.datasource.checkCodeByID(this.registrationId, this.accessToken).then(response => {
      if (response.status === ResponseStatus.SUCCESS) {
        const status = response.result?.status;
        if (status === 'verified') {
          this.registerManager.markPhoneVerified();
          return;
        }
      }
    });
  }

  requestVerification() {
    if (!this.yandexCloudManager.refreshValidation()) {
      this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);
      return;
    }

    this.setVerificationPrepareStatus(LoadingStatus.LOADING);

    this.verificationError = undefined;
    const yandexCloud = this.yandexCloudManager.data;
    const params = {
      phone: this.registerManager.phone as PhoneNumber,
      kind: CONFIRMATION_METHOD_KIND.qr,
      smart_captcha_token: this.registerManager.smartCaptchaToken as SmartCaptchaToken,
      template: 'validation' as RequestPhoneVerificationParams['template'],
      metadata: {
        ...this.registerManager.trackingManager.verificationMetadata,
        ...(yandexCloud ? { yandex_cloud: yandexCloud } : {}),
      },
    } as RequestPhoneVerificationParams;

    this.datasource
      .requestVerificationPrepare(params)
      .then(response => {
        if (response.status === ResponseStatus.SUCCESS) {
          const result = response.result;

          const id = result.verification.id;

          this.registrationId = String(id);
          this.accessToken = result.access_token ?? this.accessToken;
          this.confirmationRequestInterval = result.verification.request_interval;
          this.confirmationQrUrl =
            `tel:${this.registerManager.incomingPhone}, ${result.code}#` as Url;

          this.startConfirmationTimer(this.confirmationRequestInterval ?? 0);
        } else {
          if (response.status === ResponseStatus.SERVICE_ERROR) {
            this.verificationError = response.error.errorData?.error ?? UNEXPECTED_ERROR_KEY;
            return;
          }

          this.verificationError = UNEXPECTED_ERROR_KEY;
        }
      })
      .finally(() => this.setVerificationPrepareStatus(LoadingStatus.LOADED));
  }
}

export { QrConfirmationManager };
